import {
    EmailAuthProvider, getMultiFactorResolver, multiFactor, PhoneAuthProvider, PhoneMultiFactorGenerator,
    reauthenticateWithCredential, signInWithEmailAndPassword, TotpMultiFactorGenerator,
    type Auth, type MultiFactorError, type MultiFactorResolver, type RecaptchaVerifier, type UserCredential,
} from 'firebase/auth';

export const ACCOUNT_SECURITY_EVENT = 'inteloptics-account-security';
export const MFA_RECOVERY_EVENT = 'inteloptics-mfa-recovery';
export const MFA_LOGIN_NOTICE = 'inteloptics-mfa-login-notice';
export const openAccountSecurity = () => window.dispatchEvent(new Event(ACCOUNT_SECURITY_EVENT));
export const openMfaRecovery = (email = '') => window.dispatchEvent(new CustomEvent(MFA_RECOVERY_EVENT, { detail: { email } }));

export async function authenticateWithMfa(auth: Auth, email: string, password: string,
    challenge: (resolver: MultiFactorResolver) => Promise<UserCredential>, reauthenticate = false): Promise<UserCredential> {
    try {
        if (reauthenticate) {
            if (!auth.currentUser || auth.currentUser.email !== email)
                throw new Error('Sign in again before changing account security.');
            return await reauthenticateWithCredential(auth.currentUser, EmailAuthProvider.credential(email, password));
        }
        return await signInWithEmailAndPassword(auth, email, password);
    } catch (error) {
        if ((error as { code?: string }).code !== 'auth/multi-factor-auth-required')
            throw error;
        return challenge(getMultiFactorResolver(auth, error as MultiFactorError));
    }
}

export function resolveMfaChallenge(resolver: MultiFactorResolver, factorUid: string, code: string, verificationId?: string) {
    const factor = resolver.hints.find(hint => hint.uid === factorUid);
    if (!factor || !/^\d{6}$/.test(code))
        throw new Error('Enter the six-digit code for your selected method.');
    if (factor.factorId === TotpMultiFactorGenerator.FACTOR_ID)
        return resolver.resolveSignIn(TotpMultiFactorGenerator.assertionForSignIn(factor.uid, code));
    if (factor.factorId !== PhoneMultiFactorGenerator.FACTOR_ID || !verificationId)
        throw new Error('Request a text message before entering its code.');
    return resolver.resolveSignIn(PhoneMultiFactorGenerator.assertion(PhoneAuthProvider.credential(verificationId, code)));
}

export async function sendMfaCode(auth: Auth, verifier: RecaptchaVerifier, resolver: MultiFactorResolver, factorUid: string) {
    const hint = resolver.hints.find(factor => factor.uid === factorUid && factor.factorId === 'phone');
    if (!hint)
        throw new Error('Choose an enrolled phone number.');
    return new PhoneAuthProvider(auth).verifyPhoneNumber({ multiFactorHint: hint, session: resolver.session }, verifier);
}

export async function removeMfaMethod(auth: Auth, uid: string) {
    const user = auth.currentUser;
    if (!user || multiFactor(user).enrolledFactors.length < 2)
        throw new Error('Add and verify another method before removing this one.');
    await multiFactor(user).unenroll(uid);
}

export function mfaError(error: unknown): string {
    const code = (error as { code?: string }).code;
    switch (code) {
        case 'auth/invalid-verification-code': case 'auth/invalid-multi-factor-session':
            return 'The code is incorrect or expired. Try again or request a new code.';
        case 'auth/code-expired': case 'auth/session-expired':
            return 'This verification expired. Start again to request a new code.';
        case 'auth/too-many-requests': case 'auth/quota-exceeded':
            return 'Too many attempts. Wait before retrying, or use an enrolled authenticator app.';
        case 'auth/captcha-check-failed':
            return 'Phone verification could not complete. Retry the security check.';
        case 'auth/operation-not-allowed': case 'auth/unsupported-first-factor':
            return 'This method is unavailable. Use an authenticator app or contact support.';
        case 'auth/requires-recent-login': case 'auth/user-token-expired':
            return 'Sign in with your password and MFA again before changing security settings.';
        default:
            return error instanceof Error ? error.message : 'Verification failed. Try again.';
    }
}
